// © Microsoft Corporation. All rights reserved.

import { logger } from "../logging";

/** Perf marker start suffix */
const StartMarkSuffix = "-start";

/**
 * PerfMarker facilitates adding and measuring performance markers.
 */
class PerfMarker {
    /** The single instance of the PerfMarker class. */
    private static instance: PerfMarker;

    /** The performance object. */
    private performance: Performance;

    /** Perf marker end suffix */
    private readonly endMarkSuffix: string = "-end";

    /**
     * The constructor.
     */
    private constructor() {
        if (typeof window !== "undefined") {
            this.initialize(window.performance);
        }
    }

    /**
     * Returns singleton class instance.
     * @returns The class instance
     */
    public static getInstance(): PerfMarker {
        if (!PerfMarker.instance) {
            PerfMarker.instance = new PerfMarker();
        }

        return PerfMarker.instance;
    }

    /**
     * Initialize the member variables.
     * @param performance - The performance object instance.
     */
    public initialize(performance: Performance): void {
        this.performance = performance;
    }

    /**
     * Adds given perf marker to browser.
     * @param name - Perf marker to start
     */
    public startMark(name: string): void {
        this.singleMark(name + StartMarkSuffix);
    }

    /**
     * Adds given perf marker to browser.
     * @param name - Perf marker to end
     */
    public endMark(name: string): void {
        this.singleMark(name + this.endMarkSuffix);
        this.measureStartEnd(name);
    }

    /**
     * Adds a single performance marker via the performance API.
     * @param name - the name of the perf marker
     */
    public singleMark(name: string): void {
        const performance = this.performance;
        if (performance && performance.mark) {
            performance.mark(name);
        }
    }

    /**
     * Removes a single mark.
     * @param name - the name of the perf marker
     */
    public clearMark(name: string): void {
        const performance = this.performance;
        if (performance && performance.clearMarks) {
            performance.clearMarks(name);
        }
    }

    /**
     * Removes perf a measure
     * @param name - the name of the perf measure
     */
    public clearMeasures(name: string): void {
        const performance = this.performance;
        if (performance && performance.clearMeasures) {
            performance.clearMeasures(name);
        }
    }
    /**
     * Utility method to add start/end marker around a promise.
     * @param name - The name of perf marker
     * @param getPromise - The callback
     * @returns The promise
     */
    public async measurePromise<T>(name: string, getPromise: () => Promise<T>): Promise<T> {
        this.startMark(name);
        const result = await getPromise();
        this.endMark(name);
        return result;
    }

    /**
     * Telemetry call this method to collect existing perf markers that each Experience has added.
     * @param experienceName - Perf marker name in case of custom marker / Experience name
     * @returns {IPerfMarkers}
     */
    public measurePerfMarker(experienceName: string): IPerfMarkers {

        if (!this.performance) {
            return;
        }

        const perfMarkers: { [id: string]: PerfMarkerDetail; } = {};

        // Get all perf markers from browser.
        const perfEntries = this.performance.getEntriesByType("mark");
        if (!perfEntries) {
            return perfMarkers;
        }

        // Iterate through perfEntries received from browser.
        for (let i = 0; i < perfEntries.length; i++) {

            // if marker name in perfEntries object is null
            let markerName: string = perfEntries[i].name;
            if (!markerName || perfEntries[i].startTime == null) {
                continue;
            }

            // Remove start and end suffix.
            markerName = markerName.replace(/-(end|start)$/i, "");

            // if marker name retrieved from browser is not for this experience then continue with next marker.
            if (!markerName.startsWith(experienceName)) {
                continue;
            }

            const perfMarkerDetail = perfMarkers[markerName] || new PerfMarkerDetail(markerName);
            perfMarkerDetail.processPerfEntry(perfEntries[i]);
            if (perfMarkerDetail.isValid) {
                perfMarkers[markerName] = perfMarkerDetail;
            }
        }

        return perfMarkers;
    }

    /**
     * Measures the given marker name's start/end markers.
     * @param name - The name of the marker whose -start and -end to measure
     */
    private measureStartEnd(name: string): void {
        const performance = this.performance;
        if (performance && performance.measure) {
            try {
                performance.measure(
                    name,
                    name + StartMarkSuffix,
                    name + this.endMarkSuffix
                );
            } catch (ex) {
                // An exception will be thrown if either start/end marker didn't exist.
                // We swallo this exception and log an error so that actual code's execution
                // would not be effected due to performance measurement code.
                logger.logError(`Error while attempting to measure start/end of '${name}'. Error: ${ex}`);
            }
        }
    }
}

export const perfMarker = PerfMarker.getInstance();

/**
 * Interface defining attributes of each per marker.
 */
export interface IPerfMarkers {
    [key: string]: PerfMarkerDetail;
}

/**
 * Interface defining perf markers.
 */
class PerfMarkerDetail {
    /** marker name */
    public readonly markerName: string;
    /** first start time */
    public firstStartTime: number;
    /** start time */
    public startTime: number;
    /** duration */
    public duration = 0;

    /**
     * Sets marker name.
     */
    constructor(markerName: string) {
        this.markerName = markerName;
    }

    /**
     * Check if given marker is valid. If marker has a start time then it is a valid marker.
     * @returns True if valid else false.
     */
    get isValid(): boolean {
        return this.startTime != null;
    }

    /**
     * Sum up start and end marker duraiton per marker name.
     */
    public processPerfEntry(perfEntry: PerformanceEntry): void {

        // If given mark is a start mark
        if (perfEntry.name.endsWith(StartMarkSuffix)) {

            // If this is a start mark and firstStartTime is undefined then assign value of startTime now.
            if (!this.firstStartTime) {
                this.firstStartTime = perfEntry.startTime;
            }

            // Change start time to start time of current mark.
            this.startTime = perfEntry.startTime;

        } else if (this.startTime != null) {
            // This is is not a start mark. Assuming this is end mark, check start time of this mark is set earlier. if set properly then proceed.
            // else likely a candiddate to skip "if" part.

            // This is a end mark. Add earlier duration plus current start time minus this start time.
            this.duration += perfEntry.startTime - this.startTime;

            // reset start time so we can recalculate the next start/end pair correctly
            this.startTime = null;
        }
    }
}